import * as React from 'react';
import classNames from 'classnames';
import Label from 'box-ui-elements/es/components/label/Label';
import InlineNotice from 'box-ui-elements/es/components/inline-notice/InlineNotice';
import { FormattedMessage } from 'react-intl';
import useId from '../../utils/useId';
import DropZone from '../drop-zone';
import { type UploadFieldProps } from './types';
import css from './buie-upload-field.module.scss';
import { isFieldError, isMessageDescriptor } from './types-utils';

const UploadField = React.memo<UploadFieldProps>((props: UploadFieldProps) => {
  const {
    className = '',
    label,
    description,
    error,
    isRequired,
    isDisabled,
    onAddItems,
    onRemoveItem,
    items,
  } = props;
  const hasError = !!error;
  const classes = classNames(
    className,
    css.uploadFieldContainer,
    'upload-field-container',
  );

  const errorMessageID = useId('errorMessage');
  const descriptionID = useId('description');

  const ariaAttrs = {
    'aria-invalid': hasError,
    'aria-required': isRequired,
    'aria-describedby': description ? descriptionID : undefined,
    'aria-errormessage': errorMessageID,
  } as const;

  // TODO - accessibility still needs more work
  return (
    <div className={classes} data-testid="buie-upload-field">
      <Label text={label} />

      {!!description && (
        <div className="upload-field-description" id={descriptionID}>
          {description}
        </div>
      )}

      {hasError && (
        <InlineNotice
          className={css.inlineNotice}
          data-testid="buie-upload-inline-error"
          type="error"
        >
          {isFieldError(error) && <FormattedMessage {...error.message} />}
          {isMessageDescriptor(error) && <FormattedMessage {...error} />}
        </InlineNotice>
      )}
      <DropZone
        className="dropzone"
        isDisabled={isDisabled}
        items={items}
        onAddItems={onAddItems}
        onRemoveItem={onRemoveItem}
        {...ariaAttrs}
      />
      <span className="accessibility-hidden" id={errorMessageID} role="alert">
        {error && isFieldError(error) && (
          <FormattedMessage {...error.message} />
        )}
      </span>
    </div>
  );
});

export { UploadField };
export default UploadField;
